import React, { useCallback, useState } from 'react';
import { FaEuroSign } from 'react-icons/fa';
import { FaPercent } from 'react-icons/fa6';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import { PiEnvelopeSimple, PiPencilSimple } from 'react-icons/pi';
import { Button, Input, Textarea, Tooltip, cn } from 'rizzui';
import { toast } from 'sonner';
import Spinner from '../../../components/bootstrap/Spinner';
import CustomSelect from '../../../components/forms/CustomSelect';
import PhoneInput from '../../../components/forms/PhoneInputWrapper';
import { Loader } from '../../../components/loader/SpinnerLogo';
import useFetch from '../../../hooks/useFetch';
import FormGroup from '../../../layout/shared/form-group';
import { TutorService } from '../../../services/tutor/tutorService';
import { TutorProvidingCorporation } from '../../../type/entities/tutor-type';
import { paymentMethods, tutorType } from '../../tutors/TutorForm';
import TutorEditModal from './components/TutorEditModal';

interface TutorProfileProps { }

const TutorProfile: React.FC<TutorProfileProps> = ({ }) => {

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [downloadingCorpId, setDownloadingCorpId] = useState<string | null>(null);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await (new TutorService()).getMyProfile();
        return response.getResponseData();
    }, []));

    const handleTutorUpdate = () => {
        refetch();
        setIsEditModalOpen(false);
    };

    const handleDownloadAgreement = async (pc: TutorProvidingCorporation) => {
        if (!data?.id) return;
        try {
            setDownloadingCorpId(pc.id);
            const response = (await (new TutorService()).downloadMyAgreementDocument(data.id, pc.providingCorporation.id)).getResponseData();

            const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
            const link = document.createElement('a');
            const corporationName = (pc.providingCorporation?.name || 'impartidora').replace(/\s+/g, '-').toLowerCase();

            link.href = url;
            link.setAttribute('download', `convenio-${corporationName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar el convenio:', error);
            toast.error('Hubo un problema al descargar el convenio.');
        } finally {
            setDownloadingCorpId(null);
        }
    };

    if (loading) return <Loader height='70vh' />;

    return (
        <>
            <div className="@container">
                <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Mi Perfil
                    </h2>
                    <div className='flex gap-4'>
                        {(data?.providingCorporations || [])
                            .filter((pc: TutorProvidingCorporation) => !!pc.hasAgreementSigned)
                            .map((pc: TutorProvidingCorporation) => (
                                <Tooltip
                                    key={pc.id}
                                    content={`Descargar convenio (${pc.providingCorporation?.name || 'Impartidora'})`}
                                    placement={'top'}
                                    color={'invert'}
                                    size={'sm'}
                                >
                                    <Button
                                        variant={'outline'}
                                        color={'primary'}
                                        isLoading={downloadingCorpId === pc.id}
                                        disabled={downloadingCorpId !== null && downloadingCorpId !== pc.id}
                                        onClick={async () => {
                                            await handleDownloadAgreement(pc);
                                        }}
                                        className="w-auto"
                                    >
                                        {downloadingCorpId === pc.id ? <Spinner /> : <IoCloudDownloadOutline />}
                                    </Button>
                                </Tooltip>
                            ))}

                        <div className='border-l border-gray-300' />

                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setIsEditModalOpen(true) }}
                            className="flex items-center gap-2"
                        >
                            <PiPencilSimple className="h-4 w-4" />
                            Editar Perfil
                        </Button>
                    </div>
                </div>

                <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11">
                    <FormGroup
                        title="Información General"
                        description="Datos principales del tutor"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            readOnly
                            label="Nombre"
                            placeholder="Nombre"
                            value={data?.name ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="Apellidos"
                            placeholder="Apellidos"
                            value={data?.lastName ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="CIF/NIF"
                            placeholder="12345678Z"
                            value={data?.nifCif ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="IBAN"
                            placeholder="ES00 0000 0000 0000 0000 0000"
                            value={data?.iban ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                            type="email"
                            label="Correo Electrónico"
                            placeholder="info@attiva.es"
                            value={data?.email ?? ""}
                        />
                        <PhoneInput
                            id="telephone"
                            readOnly
                            label="Teléfono"
                            placeholder="123456789"
                            value={data?.telephone ?? ""}
                            className="flex-grow"
                            required={false}
                        />
                        <PhoneInput
                            id="telephoneSecondary"
                            readOnly
                            label="Teléfono Secundario"
                            placeholder="123456789"
                            value={data?.telephoneSecondary ?? ""}
                            className="flex-grow"
                            required={false}
                        />
                        <CustomSelect
                            readOnly
                            id="tutorType"
                            label="Tipo de Tutor"
                            value={{
                                value: data?.tutorType,
                                label: tutorType.find((type) => type.value === data?.tutorType)?.label,
                            }}
                        />
                        {data?.tutorType === 'external' && (
                            <Input
                                readOnly
                                label="Retenciones"
                                placeholder="0"
                                value={data?.withholding ?? ""}
                                suffix={<FaPercent />}
                                className="flex-grow"
                            />
                        )}
                        <Input
                            readOnly
                            label="Usuario de la Plataforma"
                            placeholder="username"
                            value={data?.platformUser ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="Contraseña de la Plataforma"
                            placeholder="password"
                            value={data?.platformPassword ?? ""}
                            className="flex-grow"
                        />
                    </FormGroup>

                    <FormGroup
                        title="Cursos"
                        description="Cursos que el tutor puede impartir"
                        className={cn("pt-3 pb-4", data?.products?.length === 0 && "hidden")}
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-3"
                    >
                        {data?.products?.map((product: any, index: number) => (
                            <div key={product.id} className={cn("grid grid-cols-1 md:grid-cols-3 gap-3 pb-5", index !== data.products.length - 1 && "border-b border-gray-200")}>
                                <Input
                                    readOnly
                                    label="Curso"
                                    placeholder="Curso"
                                    value={product.product.name}
                                    className="md:col-span-2"
                                />
                                <Input
                                    readOnly
                                    label="Precio (€)"
                                    placeholder="Precio"
                                    value={product.pricePerStudent}
                                    suffix={<FaEuroSign />}
                                    step={0.01}
                                />
                                <Input
                                    readOnly
                                    id="paymentMethod"
                                    label="Método de Pago"
                                    value={paymentMethods.find((type) => type.value === product.paymentMethod)?.label}
                                />
                                <Input
                                    readOnly
                                    label="Retenciones"
                                    placeholder="Retenciones"
                                    suffix={<FaPercent />}
                                    value={product.deductions}
                                />
                            </div>
                        ))}
                    </FormGroup>

                    <FormGroup
                        title="Información Adicional"
                        description="Datos adicionales del tutor"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-5"
                    >
                        <Input
                            readOnly
                            label="Dirección"
                            placeholder="Calle de la forja, 123"
                            value={data?.address ?? ""}
                            className="md:col-span-2"
                        />
                        <Input
                            readOnly
                            label="Población"
                            placeholder="Córdoba"
                            value={data?.town ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="Provincia"
                            placeholder="Madrid"
                            value={data?.province ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="Código Postal"
                            placeholder="12345"
                            value={data?.postalCode ?? ""}
                            className="flex-grow"
                        />
                        <Textarea
                            readOnly
                            id="comments"
                            label="Comentarios"
                            className="md:col-span-4 [&>label>span]:font-medium"
                            value={data?.comments}
                            rows={3}
                        />
                    </FormGroup>
                </div>
            </div>

            <TutorEditModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                tutor={data}
                onTutorUpdate={handleTutorUpdate}
            />
        </>
    );
};

export default TutorProfile;
